import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import AppContext from 'context/Context';

const TinymceEditor = ({ value, handleChange }) => {
  const {
    config: { isDark, isRTL }
  } = useContext(AppContext);
  const editorRef = useRef(null);

  const applyBodyColor = editor => {
    if (editor && editor.dom) {
      // NOTE: we intentionally use the `isDark` flag directly instead of
      // reading the `--falcon-black`/`--falcon-white` CSS variables here.
      // Those variables are swapped by a *different* component's effect
      // (useToggleStyle, in Main.js) whenever the `dark` class is
      // toggled on <html>. Because React runs effects bottom-up
      // (descendants before ancestors) within a single commit, this
      // editor's effect would otherwise run before that class/variable
      // swap happens, always reading the color from the *previous*
      // theme. Using the boolean directly avoids that race entirely.
      editor.dom.addStyle(
        `body{color: ${isDark ? '#ffffff' : '#000000'} !important;}`
      );
    }
  };

  useEffect(() => {
    applyBodyColor(editorRef.current);
  }, [isDark]);

  return (
    <Editor
      onInit={(evt, editor) => {
        editorRef.current = editor;
        applyBodyColor(editor);
      }}
      value={value}
      onEditorChange={handleChange}
      apiKey={process.env.REACT_APP_TINYMCE_APIKEY}
      init={{
        skin: 'oxide',
        content_css: 'default',
        skin_url: '/tinymce/skins/ui/oxide',
        content_css_cors: true,
        height: '30vh',
        menubar: false,
        content_style: `body { color: ${
          isDark ? '#ffffff' : '#000000'
        }, font-family: 'Inter', sans-serif; font-size: 1rem; }`,
        mobile: {
          theme: 'mobile',
          toolbar: ['undo', 'bold']
        },
        statusbar: false,
        plugins: 'link image lists table media directionality',
        toolbar:
          'styleselect | bold italic link bullist numlist image blockquote table media undo redo',

        directionality: isRTL ? 'rtl' : 'ltr',
        theme_advanced_toolbar_align: 'center'
      }}
    />
  );
};

TinymceEditor.propTypes = {
  value: PropTypes.string,
  handleChange: PropTypes.func
};

export default TinymceEditor;
